import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { Account } from "@/commons/services/Authorization";
import { Permissions } from "@/commons/services/Authorization";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 一般ユーザーアカウント API 操作クラス
 */
export default class StandardAccounts {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET StandardAccounts
   */
  public async get(userStore: LoginUser, queryParams?: { startIndex: number; endIndex: number; all: string }): Promise<Array<StandardAccountsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Accounts, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<StandardAccountsGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET StandardAccountsCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Accounts, formatParams);
    const response = await this.commonService.executeGet<number>(userStore, `${requestUrl}/count`, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST StandardAccounts
   */
  public async post(userStore: LoginUser, requestBody: StandardAccountsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.AccountsRegister, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);
    return response.data;
  }

  /**
   * PATCH StandardAccounts
   */
  public async patch(userStore: LoginUser, user: StandardAccountsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Accounts, formatParams);

    // APIのモデルに合わせてここで送信内容を調整
    const requestBody: StandardAccountsPatchModel = {
      userId: user.userId,
      updateType: user.updateType,
      permissions: user.permissions,
      updated: user.updated
    };

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE StandardAccounts
   *
   * - DELETEだが、userIdをURLに含めたくないのでPOSTで送信する
   */
  public async delete(userStore: LoginUser, userId: string): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };

    // APIのモデルに合わせてここで送信内容を調整
    const requestBody: any = {
      userId: userId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.AccountsDelete, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST ExistAccounts
   *
   * - 実際には取得処理だが、ユーザーIDをクエリパラメータで渡したくない
   * - そのため、POSTにしてリクエストボディでユーザーIDを渡す
   */
  public async exist(userStore: LoginUser, userId: string): Promise<{ exist: boolean; existAccount: boolean }> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    // 引数のuserIdが無ければstoreから取得したものを使用する
    const requestUrl: string = UrlFormatter.format(RestUrl.ExistAccount, formatParams);
    const response = await this.commonService.executePost<{ exist: boolean; existAccount: boolean }>(
      userStore,
      requestUrl,
      { userId: userId },
      this.constructor.name,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }
}

export interface StandardAccountsGetModel extends Account {}

export interface StandardAccountsPostModel {
  /** ユーザーID（メールアドレスのみ） */
  userId: string;
  /** パスワード */
  password?: string;
  /**  */
  needRegisterForCognito?: boolean;
  /** 氏名 */
  name?: string;
  /** 氏名（かな） */
  kana?: string;
  /** 電話番号 */
  tel?: string;
  /** メールアドレス（通知用） */
  mail?: string;
  /** 各種機能権限 */
  permissions: Permissions;
}

export interface StandardAccountsPatchModel {
  /** ユーザーID */
  userId: string;
  /** 更新種別 */
  updateType: UpdateType;
  /** 権限 */
  permissions: Permissions;
  /** 更新日時 */
  updated: number;
}

export enum UpdateType {
  MINE = 0,
  OTHER = 1
}
